import type { PropertySearchQuery } from '../helpers/useSearchQueries';
import { priceChipLabel } from '../map/refresh/priceScale';
import { formatLotSize } from '../property/helpers';
import type {
  SavedSearchFieldsFragment,
  SavedSearchPropertyType,
} from './__generated__/saved-searches.zeus';
import { SUPPORTED_PROPERTY_TYPES } from './mapping';

const formatPriceShort = (dollars: number): string => `$${Math.round(dollars / 1000)}k`;

const formatPriceRange = (min: number | null | undefined, max: number | null | undefined): string =>
  [min ? formatPriceShort(min) : '', max ? formatPriceShort(max) : ''].filter(Boolean).join('–');

const formatMarketSlug = (slug: string): string =>
  slug
    .split(/[-_]/)
    .filter((word) => word.length > 0)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');

export function formatSavedSearchName(
  query: PropertySearchQuery,
  locationLabel?: string | null,
): string {
  const parts: string[] = [];
  if (query.bedrooms) {
    parts.push(`${query.bedrooms}${query.maxBedrooms === query.bedrooms ? '' : '+'} bd`);
  }
  if (query.bathrooms) {
    parts.push(`${query.bathrooms}+ ba`);
  }
  if (query.minListPrice || query.maxListPrice) {
    parts.push(formatPriceRange(query.minListPrice, query.maxListPrice));
  }
  if (query.hasPool) {
    parts.push('pool');
  }
  const label = locationLabel || (query.market ? formatMarketSlug(query.market) : null);
  if (label) {
    // "in" only reads well after a filter: "3+ bd, in Phoenix", but just "Phoenix".
    parts.push(parts.length > 0 ? `in ${label}` : label);
  }
  return parts.length > 0 ? parts.join(', ') : 'My saved search';
}

const PROPERTY_TYPE_LABELS = {
  HOME: 'Houses',
  CONDO: 'Condos',
  TOWNHOME: 'Townhomes',
} satisfies Record<SavedSearchPropertyType, string>;

const formatSqft = (sqft: number): string => sqft.toLocaleString('en-US');

export function describeSavedSearch(saved: SavedSearchFieldsFragment): string {
  const parts: string[] = [];

  const zoneNames = saved.location.zones.map((zone) => zone.name).filter(Boolean);
  if (zoneNames.length > 0) {
    parts.push(zoneNames.join(', '));
  } else if (saved.location.vertices.length > 0) {
    parts.push('Custom map area');
  } else if (saved.location.market) {
    parts.push(formatMarketSlug(saved.location.market));
  }

  const f = saved.filters;
  if (f.minBedrooms) {
    if (f.maxBedrooms === f.minBedrooms) {
      parts.push(`${f.minBedrooms} bd`);
    } else if (f.maxBedrooms) {
      parts.push(`${f.minBedrooms}–${f.maxBedrooms} bd`);
    } else {
      parts.push(`${f.minBedrooms}+ bd`);
    }
  } else if (f.maxBedrooms) {
    parts.push(`Up to ${f.maxBedrooms} bd`);
  }
  if (f.minBathrooms) {
    parts.push(`${f.minBathrooms}+ ba`);
  }
  if (f.minPriceDollars || f.maxPriceDollars) {
    parts.push(formatPriceRange(f.minPriceDollars, f.maxPriceDollars));
  }
  if (f.minSqft || f.maxSqft) {
    const min = f.minSqft ? formatSqft(f.minSqft) : '';
    const max = f.maxSqft ? formatSqft(f.maxSqft) : '';
    parts.push(`${[min, max].filter(Boolean).join('–')} sqft`);
  }
  const types = f.propertyTypes;
  if (types.length > 0 && types.length < SUPPORTED_PROPERTY_TYPES.length) {
    parts.push(types.map((type) => PROPERTY_TYPE_LABELS[type]).join('/'));
  }
  if (f.hasPoolOnly) {
    parts.push('Pool');
  }
  if (f.singleStoryOnly) {
    parts.push('Single story');
  }
  if (f.selfTourableOnly) {
    parts.push('Self-tour');
  }
  if (f.maxMonthlyHoaFeeDollars === 0) {
    parts.push('No HOA');
  }

  return parts.length > 0 ? parts.join(' · ') : 'All homes';
}

const formatChipRange = (min: string | null, max: string | null, unit = ''): string | null => {
  if (min && max) return `${min}-${max}${unit}`;
  if (max) return `Up to ${max}${unit}`;
  if (min) return `${min}+${unit}`;
  return null;
};

const formatLotChip = (min: number | null, max: number | null): string | null => {
  const minLot = formatLotSize(min);
  const maxLot = formatLotSize(max);
  if (minLot && maxLot) return `Lot ${minLot} - ${maxLot}`;
  if (maxLot) return `Lot up to ${maxLot}`;
  if (minLot) return `Lot ${minLot}+`;
  return null;
};

const formatYearBuiltChip = (min: number | null, max: number | null): string | null => {
  if (min && max) return `Built ${min}-${max}`;
  if (max) return `Built ${max} or earlier`;
  if (min) return `Built ${min}+`;
  return null;
};

/**
 * The filters of a saved search as short chips, such as "Up to 500k" and
 * "2+ bd". Every stored filter gets a chip, so "All homes" means the search
 * has none. The location stays out: the search name usually names it.
 */
export function savedSearchFilterChips(saved: SavedSearchFieldsFragment): string[] {
  const f = saved.filters;
  const chips: (string | null)[] = [];
  if (f.minPriceDollars || f.maxPriceDollars) {
    // The same label as the price chip of the map filter bar.
    chips.push(priceChipLabel(f.minPriceDollars || null, f.maxPriceDollars || null));
  }
  const types = f.propertyTypes;
  if (types.length > 0 && types.length < SUPPORTED_PROPERTY_TYPES.length) {
    chips.push(types.map((type) => PROPERTY_TYPE_LABELS[type]).join('/'));
  }
  if (f.singleStoryOnly) chips.push('Single story');
  if (f.hasPoolOnly) chips.push('Pool');
  if (f.selfTourableOnly) chips.push('Self-tour');
  if (f.opendoorOwnedOnly) chips.push('Opendoor homes');
  if (f.fhaEligibleOnly) chips.push('FHA eligible');
  if (f.maxMonthlyHoaFeeDollars === 0) {
    chips.push('No HOA');
  } else if (f.maxMonthlyHoaFeeDollars) {
    chips.push(`HOA up to $${f.maxMonthlyHoaFeeDollars.toLocaleString('en-US')}/mo`);
  }
  if (f.minBedrooms && f.maxBedrooms === f.minBedrooms) {
    chips.push(`${f.minBedrooms} bd`);
  } else {
    chips.push(
      formatChipRange(
        f.minBedrooms ? String(f.minBedrooms) : null,
        f.maxBedrooms ? String(f.maxBedrooms) : null,
        ' bd',
      ),
    );
  }
  chips.push(
    formatChipRange(
      f.minBathrooms ? String(f.minBathrooms) : null,
      f.maxBathrooms ? String(f.maxBathrooms) : null,
      ' ba',
    ),
    formatChipRange(
      f.minSqft ? formatSqft(f.minSqft) : null,
      f.maxSqft ? formatSqft(f.maxSqft) : null,
      ' sqft',
    ),
    formatLotChip(f.minLotSqft ?? null, f.maxLotSqft ?? null),
    formatYearBuiltChip(f.minYearBuilt ?? null, f.maxYearBuilt ?? null),
  );
  const filled = chips.filter((chip): chip is string => chip != null);
  return filled.length > 0 ? filled : ['All homes'];
}
